<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="localVisible"
    width="500px"
    :close-on-click-modal="false"
    append-to-body
    @close="handleClose"
  >
    <el-form
      ref="areaForm"
      :model="formData"
      :rules="formRules"
      label-width="100px"
    >
      <el-form-item
        label="区域名称"
        prop="areaName"
      >
        <el-input
          v-model="formData.areaName"
          placeholder="请输入区域名称"
          maxlength="100"
        />
      </el-form-item>
      <el-form-item
        label="区域标识"
        prop="areaCode"
      >
        <el-input
          v-model.trim="formData.areaCode"
          placeholder="例如：A"
          maxlength="20"
          @input="handleAreaCodeInput"
        />
        <div class="form-hint">
          可选，用于标识当前区域；仅支持字母、数字、下划线和中划线
        </div>
      </el-form-item>
      <el-form-item
        label="父级区域"
        prop="parentId"
      >
        <AreaCascader
          v-model="formData.parentId"
          :options="parentOptions"
          placeholder="无 (作为根节点)"
          clearable
          style="width: 100%"
          :disabled="type === 'addChild' || type === 'edit'"
        />
      </el-form-item>
      <el-form-item
        label="层级类型"
        prop="levelTypeName"
      >
        <el-autocomplete
          v-model="formData.levelTypeName"
          :fetch-suggestions="queryLevelTypes"
          placeholder="如：园区、场馆、楼层、功能区等"
          style="width: 100%"
          @select="handleLevelTypeSelect"
          @blur="validateLevelType"
        />
        <div class="form-hint">
          可自定义层级名称，常用如：园区、场馆、楼栋、单元、楼层、房间、通道、广场、出入口、设备区等
        </div>
      </el-form-item>
      <el-form-item label="层级深度">
        <el-input
          :value="computedLevelDepth"
          placeholder="根据父级自动计算"
          disabled
        />
        <div class="form-hint">
          系统根据父级区域自动计算层级深度 (L1-L4)
        </div>
      </el-form-item>
      <el-form-item
        label="区域说明"
        prop="description"
      >
        <el-input
          v-model="formData.description"
          type="textarea"
          :rows="3"
          placeholder="请输入区域说明（可选）"
          maxlength="500"
        />
      </el-form-item>
    </el-form>
    <div slot="footer">
      <el-button @click="handleClose">
        取消
      </el-button>
      <el-button
        type="primary"
        :loading="submitting"
        @click="handleSubmit"
      >
        保存
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { areaService } from './area.service';
import AreaCascader from '@/features/gnmk/components/AreaCascader.vue';

export default {
  name: 'AreaDialog',
  components: { AreaCascader },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'add'
    },
    nodeData: {
      type: Object,
      default: null
    },
    treeData: {
      type: Array,
      default: () => []
    },
    initialParentId: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      localVisible: false,
      submitting: false,
      formData: {
        uuid: '',
        areaName: '',
        areaCode: '',
        parentId: '',
        levelTypeName: '',
        description: ''
      },
      formRules: {
        areaName: [{ required: true, message: '请输入区域名称', trigger: 'blur' }],
        areaCode: [{ pattern: /^[A-Za-z0-9_-]{1,20}$/, message: '区域标识仅支持字母、数字、下划线和中划线，长度不超过20位', trigger: 'blur' }],
        levelTypeName: [{ required: true, message: '请输入/选择层级类型', trigger: 'blur' }]
      },
      levelTypeOptions: ['园区', '场馆', '楼栋', '单元', '楼层', '房间', '通道', '广场', '出入口', '设备区', '功能区', '展区', '控制室', '室外区域']
    };
  },
  computed: {
    dialogTitle () {
      const titles = {
        add: '新增区域',
        addChild: '新增子区域',
        edit: '编辑区域'
      };
      return titles[this.type] || '区域信息';
    },
    parentOptions () {
      return this.treeData;
    },
    computedLevelDepth () {
      if (!this.formData.parentId) {
        return 'L1 (第1层)';
      }
      const parentDepth = this.findNodeDepth(this.treeData, this.formData.parentId);
      if (parentDepth && parentDepth < 4) {
        return `L${parentDepth + 1} (第${parentDepth + 1}层)`;
      }
      return '—';
    }
  },
  methods: {
    initForm () {
      this.$nextTick(() => {
        if (this.$refs.areaForm) {
          this.$refs.areaForm.resetFields();
        }

        if (this.type === 'edit' && this.nodeData) {
          this.formData = {
            uuid: this.nodeData.uuid,
            areaName: this.nodeData.areaName,
            areaCode: this.nodeData.areaCode || '',
            parentId: this.nodeData.parentId || '',
            levelTypeName: this.nodeData.levelTypeName,
            description: this.nodeData.description
          };
        } else {
          this.resetFormData();
          if (this.type === 'addChild' && this.initialParentId) {
            this.formData.parentId = this.initialParentId;
          }
        }
      });
    },

    resetFormData () {
      this.formData = {
        uuid: '',
        areaName: '',
        areaCode: '',
        parentId: '',
        levelTypeName: '',
        description: ''
      };
    },

    findNodeDepth (nodes, uuid) {
      for (const node of nodes) {
        if (node.uuid === uuid) {
          return node.levelDepth;
        }
        if (node.children && node.children.length > 0) {
          const found = this.findNodeDepth(node.children, uuid);
          if (found) return found;
        }
      }
      return null;
    },

    queryLevelTypes (queryString, cb) {
      const results = queryString
        ? this.levelTypeOptions.filter(item => item.indexOf(queryString) >= 0)
        : this.levelTypeOptions;
      cb(results.map(item => ({ value: item })));
    },

    handleLevelTypeSelect () {
      this.$refs.areaForm && this.$refs.areaForm.validateField('levelTypeName');
    },

    validateLevelType () {
      this.$refs.areaForm && this.$refs.areaForm.validateField('levelTypeName');
    },

    handleAreaCodeInput (value) {
      this.formData.areaCode = value.toUpperCase();
    },

    handleClose () {
      this.$emit('update:visible', false);
      this.$emit('close');
    },

    handleSubmit () {
      this.$refs.areaForm.validate(valid => {
        if (!valid) return;

        this.submitting = true;
        const submitData = {
          ...this.formData
        };

        const request = this.type === 'edit'
          ? areaService.updateArea(submitData)
          : areaService.addArea(submitData);

        request.then(() => {
          this.$message.success('保存成功');
          this.$emit('success');
          this.handleClose();
        }).finally(() => {
          this.submitting = false;
        });
      });
    }
  },
  watch: {
    visible (val) {
      this.localVisible = val;
      if (val) {
        this.initForm();
      }
    }
  }
};
</script>

<style scoped lang="scss">
.form-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
  line-height: 1.5;
}

::v-deep .el-form-item {
  margin-bottom: 18px;
}

::v-deep .el-form-item__error {
  position: relative;
  top: 0;
  padding-top: 2px;
}
</style>
